import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { VideoStream, VideoStreamType } from '../../store/models/camera-with-media.model';
import { Store } from '@ngrx/store';
import * as fromCameras from 'src/app/modules/cop/store/cameras';
import { CdkDragMove } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import * as fromCop from 'src/app/modules/cop/store/cop';
import { takeUntil } from "rxjs/operators";

@Component({
    selector: 'topo-video-window',
    templateUrl: './video-window.component.html',
    styleUrls: ['./video-window.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class VideoWindowComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input() videoStream: VideoStream;
  @ViewChild('resizeBox') resizeBox: ElementRef;
  @ViewChild('dragHandleCorner') dragHandleCorner: ElementRef;
  @ViewChild('streamContainer') streamContainer: ElementRef;
  @ViewChild('header') header: ElementRef;

  get resizeBoxElement(): HTMLElement {
    return this.resizeBox.nativeElement;
  }

  get dragHandleCornerElement(): HTMLElement {
    return this.dragHandleCorner.nativeElement;
  }

  destroy$ = new Subject<void>();
  refreshImageRefreshId: any;
  refreshImageUrl: string;
  width = 411;
  height = 300;
  zoomToDocumentEnabled = false;
  VideoStreamType = VideoStreamType;
  refreshVmsStreamUrl: string;
  minHeight = 300;
  refreshVideoStreamRefreshId: any;
  icon: string;
  cameraId;
  pc;
  sessionId;
  serverUrl;
  stunServer;
  token;
  candidates = [];
  videoPlayerId = uuidv4();
  checkInId: any;
  setToken: any;

  constructor(private store: Store<any>, private ngZone: NgZone, private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.token = null;
    if (this.videoStream.type == VideoStreamType.Traffic && !this.videoStream.fromCameras) {
      this.icon = 'icon-small_solid_car';
    } else {
      this.icon = 'traffic-camera';
    }

    if (!!this.videoStream.imageUrl) {
      this.refreshImageUrl = this.videoStream.imageUrl;

      var refreshRate = this.videoStream.imageRefreshRate ? this.videoStream.imageRefreshRate : 10000;
      this.refreshImageRefreshId = setInterval(() => {
        this.refreshImage();
        this.changeDetector.detectChanges();
      }, refreshRate);
    }

    if (this.videoStream.document && this.videoStream.layerUId && this.videoStream.fromCameras && this.videoStream.hasGeometry) {
      this.zoomToDocumentEnabled = true;
    }

    if (this.videoStream.type == VideoStreamType.Milestone) {
      this.serverUrl = this.videoStream.serverUrl;
      this.cameraId = this.videoStream.cameraId;
      this.token = this.videoStream.token;
      this.stunServer = this.videoStream.stunServer;

      this.setupMilestoneStream();
    }

    if (this.videoStream.type == VideoStreamType.IP) {
      this.refreshImageUrl = this.videoStream.videoUrl;
    }

    if (this.videoStream.type == VideoStreamType.LiveView) {
      this.cameraId = this.videoStream.cameraId;

      this.store.dispatch(fromCameras.getLiveViewToken({ pipelineDefinitionUId: this.videoStream.pipelineDefinitionUId }));

      this.setToken = setInterval(() => {
        this.store.dispatch(fromCameras.getLiveViewToken({ pipelineDefinitionUId: this.videoStream.pipelineDefinitionUId }));
      }, 3300000);
    }

    var refreshTime = 0;

    if (this.videoStream.type == VideoStreamType.Traffic) {
      refreshTime = 1800000; //30 minutes in milliseconds
    }

    if (this.videoStream.type == VideoStreamType.Milestone) {
      refreshTime = 900000; //15 minutes in milliseconds
    }

    if (refreshTime > 0) {
      this.refreshVideoStreamRefreshId = setInterval(() => {
        this.requestNewVideoStream();
        this.changeDetector.detectChanges();
      }, refreshTime);
    }

    this.changeDetector.detectChanges();

    window.addEventListener(
      'focus',
      () => {
        var videoPlayer = document.querySelector(`#video-${this.videoPlayerId}`) as HTMLMediaElement;
        if (videoPlayer != null) {
          videoPlayer.src = this.videoStream.videoUrl;
        }
      },
      false
    );

    this.store
      .select(fromCameras.selectLiveViewToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe(token => {
        if (!this.token && !!token) {
          this.token = token;
          this.setupLvtStream();
          this.changeDetector.markForCheck();
        }
        else {
          this.token = token;
        }
      });
  }

  ngAfterViewInit() {
    this.setAllHandleTransform();
  }

  ngAfterViewChecked(): void {
    this.setAllHandleTransform();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.pc) {
      this.pc.close();
    }

    if (this.refreshImageRefreshId) {
      clearInterval(this.refreshImageRefreshId);
      this.refreshImageRefreshId = null;
    }

    if (this.refreshVideoStreamRefreshId) {
      clearInterval(this.refreshVideoStreamRefreshId);
      this.refreshVideoStreamRefreshId = null;
    }

    if (this.checkInId){
      clearInterval(this.checkInId);
      this.checkInId = null;
    }

    if (this.setToken){
      clearInterval(this.setToken);
      this.setToken = null;
    }
  }

  setAllHandleTransform() {
    this.minHeight = this.streamContainer.nativeElement.offsetHeight + this.header.nativeElement.offsetHeight + 45;
    this.height = this.minHeight;
    const rect = this.resizeBoxElement.getBoundingClientRect();
    this.setHandleTransform(this.dragHandleCornerElement, rect);
    this.changeDetector.detectChanges();
  }

  setHandleTransform(dragHandle: HTMLElement, targetRect: ClientRect | DOMRect) {
    const dragRect = dragHandle.getBoundingClientRect();
    const translateX = targetRect.width - dragRect.width;
    const translateY = targetRect.height - dragRect.height;
    dragHandle.style.transform = `translate(${translateX}px, ${translateY}px)`;
    this.changeDetector.detectChanges();
  }

  dragMove(dragHandle: HTMLElement, $event: CdkDragMove<any>) {
    this.ngZone.runOutsideAngular(() => {
      this.resize(dragHandle, this.resizeBoxElement);
    });
    this.changeDetector.detectChanges();
  }

  resize(dragHandle: HTMLElement, target: HTMLElement) {
    const dragRect = dragHandle.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    const width = dragRect.left - targetRect.left + dragRect.width;
    const height = dragRect.top - targetRect.top + dragRect.height;

    this.width = width;
    this.height = height;
    this.minHeight = this.streamContainer.nativeElement.offsetHeight + this.header.nativeElement.offsetHeight + 45;

    target.style.width = width + 'px';
    target.style.height = height + 'px';

    this.setAllHandleTransform();
    this.changeDetector.detectChanges();
  }

  refreshImage() {
    if (this.videoStream.imageUrl) {
      this.refreshImageUrl = this.videoStream.imageUrl + '&date=' + new Date().getTime();
    }
    this.changeDetector.detectChanges();
  }

  closeWindow() {
    this.store.dispatch(fromCameras.hideVideoWindow({ videoStream: this.videoStream }));
  }

  zoomToDocument() {
    if (this.videoStream.fromCameras) {
      this.store.dispatch(
        fromCop.zoomToDocument({
          layer: this.videoStream.layer,
          dataset: this.videoStream.dataset,
          document: this.videoStream.document
        })
      );
    }
    this.changeDetector.detectChanges();
  }

  requestNewVideoStream() {
    if (this.videoStream.type == VideoStreamType.Milestone) {
      this.store.dispatch(
        fromCameras.getVmsCameraMedia({ integrationKey: this.videoStream.integrationKey, cameraId: this.cameraId, mediaWidths: [389] })
      );
    }

    if (this.videoStream.type == VideoStreamType.Traffic) {
      this.store.dispatch(fromCameras.refreshVideoStream({ videoStream: this.videoStream }));
    }

    this.changeDetector.detectChanges();
  }

  async setupMilestoneStream() {
    if (this.pc != null) await this.pc.close();

    this.pc = this.stunServer ? new RTCPeerConnection({ iceServers: [{ urls: this.stunServer }] }) : new RTCPeerConnection();

    this.pc.ontrack = evt => ((document.querySelector('#video-' + this.videoPlayerId) as HTMLMediaElement).srcObject = evt.streams[0]);
    this.pc.onicecandidate = evt => evt.candidate && this.sendIceCandidate(JSON.stringify(evt.candidate));
    this.pc.onconnectionstatechange = async () => {
      if (this.pc.connectionState === 'disconnected') {
        console.log('attempting to reestablish connection');
        await this.setupMilestoneStream();
      }
    };

    this.initiateWebRTCSession();
  }

  async initiateWebRTCSession() {
    // Initiate WebRTC session on the server
    const body = { cameraId: this.cameraId, resolution: 'notInUse' };
    const response = await fetch(this.serverUrl + '/WebRTCSession', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.token
      },
      body: JSON.stringify(body)
    });
    const json = await response.json();
    this.sessionId = json['sessionId'];

    // Update answerSDP value on the server
    await this.pc.setRemoteDescription(new RTCSessionDescription(JSON.parse(json['offerSDP'])));
    this.pc
      .createAnswer()
      .then(answer => this.pc.setLocalDescription(answer))
      .then(() => this.updateAnswerSDP(json, JSON.stringify(this.pc.localDescription)));
    this.addServerIceCandidates();
  }

  async updateAnswerSDP(data, localDescription) {
    data['answerSDP'] = localDescription;
    await fetch(this.serverUrl + '/WebRTCSession', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.token
      },
      body: JSON.stringify(data)
    });
  }

  async addServerIceCandidates() {
    if (!this.sessionId) {
      return;
    }

    const response = await fetch(this.serverUrl + '/IceCandidates/' + this.sessionId, {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + this.token
      }
    });

    const json = await response.json();
    for (const element of json['candidates']) {
      if (!this.candidates.includes(element)) {
        this.candidates.push(element);
        var obj = JSON.parse(element);
        await this.pc.addIceCandidate(obj);
      }
    }

    if (this.pc.iceConnectionState == 'new' || this.pc.iceConnectionState == 'checking') {
      setTimeout(this.addServerIceCandidates, 20);
    }
  }

  async sendIceCandidate(candidate) {
    const body = { sessionId: this.sessionId, candidates: [candidate] };
    await fetch(this.serverUrl + '/IceCandidates', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.token
      },
      body: JSON.stringify(body)
    });
  }

  async setupLvtStream() {
    if (!this.cameraId) {
      return;
    }

    const body = { protocol: 'webrtc' };
    const response = await fetch(`https://api.lvt.com/v1/cameras/${this.cameraId}/streams`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.token
      },
      body: JSON.stringify(body)
    });
    const json = await response.json();

    var streamId = json['streamId'];
    var refreshInterval = json['refreshInterval'];

    if (!refreshInterval) {
      return;
    }

    var signalUrl = json['signalUrl'];
    var streamInfo = json['streamInfo'];

    this.checkInId = setInterval(() => {
      this.refreshLvtStream(streamId);
    }, refreshInterval);

    const ws = new WebSocket(signalUrl)
    this.pc = new RTCPeerConnection()

    const getOffer = () => {
      const message = {
        direction: 'play',
        command: 'getOffer',
        streamInfo: streamInfo,
      };

      ws.send(JSON.stringify(message));
    }

    ws.addEventListener('open', () => {
      getOffer();
    });

    ws.addEventListener('message', (messageEvent) => {
      const data = JSON.parse(messageEvent.data);
      const status = Number(data.status);

      if (status == 502 || status == 504) {
        setTimeout(getOffer, 1000);
      }

      if (status == 200) {
        // step 1
        if (data.streamInfo && data.streamInfo.sessionId) {
          streamInfo.sessionId = data.streamInfo.sessionId;
        }
      }

      // step 2
      if (data.sdp) {
        this.pc.setRemoteDescription(new RTCSessionDescription(data.sdp))
          // step 3
          .then(() => this.pc.createAnswer())
          .then((description) => this.pc.setLocalDescription(description))
          .then(() => {
            const message = {
              direction: 'play',
              command: 'sendResponse',
              sdp: this.pc.localDescription,
              streamInfo: streamInfo
            };

            ws.send(JSON.stringify(message));
          });
      }

      // step 4
      if (data.iceCandidates) {
        for (const ic of data.iceCandidates) {
          this.pc.addIceCandidate(new RTCIceCandidate(ic));
        }
      }
    })

    this.pc.ontrack = evt => {
      ((document.querySelector('#video-' + this.videoPlayerId) as HTMLMediaElement).srcObject = evt.streams[0])
    };
  }

  async refreshLvtStream(streamId: string) {
    await fetch(`https://api.lvt.com/v1/streams/${streamId}:checkIn`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.token
      }
    });
  }
}
